/**
 * Created: 2026-03-21
 * Initial commit: Add dashboard page with daily income chart
 *
 * Dashboard page showing daily income chart for the selected date range.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Alert, Box, Button, ButtonGroup, Typography } from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import type { DateRange } from '@mui/x-date-pickers-pro/models'
import { endOfDay, startOfDay, startOfMonth, subDays } from 'date-fns'
import { useState } from 'react'
import { DailyIncomeDocument } from '../gql/graphql'
import { useSiteContext } from '../hooks'

gql`
  query DailyIncome($siteId: UUID!, $startDate: DateTime!, $endDate: DateTime!) {
    dailyIncome(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      date
      amount
    }
  }
`

const shortcutButtons: { label: string; getRange: () => DateRange<Date> }[] = [
  {
    label: '近7天',
    getRange: () => {
      const end = new Date()
      return [subDays(end, 6), end]
    },
  },
  {
    label: '本月',
    getRange: () => [startOfMonth(new Date()), new Date()],
  },
  {
    label: '近30天',
    getRange: () => {
      const end = new Date()
      return [subDays(end, 29), end]
    },
  },
]

function defaultRange(): DateRange<Date> {
  const end = new Date()
  return [subDays(end, 6), end]
}

export default function DashboardPage() {
  const { currentSite } = useSiteContext()
  const [range, setRange] = useState<DateRange<Date>>(defaultRange)

  const [startDate, endDate] = range
  const queryStart = startDate ? startOfDay(startDate).toISOString() : null
  const queryEnd = endDate ? endOfDay(endDate).toISOString() : null

  const { data, loading, error } = useQuery(DailyIncomeDocument, {
    variables: {
      siteId: currentSite?.id ?? '',
      startDate: queryStart ?? '',
      endDate: queryEnd ?? '',
    },
    skip: !currentSite || !queryStart || !queryEnd,
    fetchPolicy: 'network-only',
  })

  const points = data?.dailyIncome ?? []
  const xLabels = points.map((p) => p.date)
  const amounts = points.map((p) => p.amount)
  const totalIncome = amounts.reduce((sum, v) => sum + v, 0)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">数据看板</Typography>
        <Box sx={{ flex: 1 }} />
        <ButtonGroup size="small" variant="outlined">
          {shortcutButtons.map((s) => (
            <Button key={s.label} onClick={() => setRange(s.getRange())}>
              {s.label}
            </Button>
          ))}
        </ButtonGroup>
        <DateRangePicker
          value={range}
          onChange={setRange}
          format="yyyy-MM-dd"
          maxDate={new Date()}
          localeText={{ start: '开始日期', end: '结束日期' }}
          slotProps={{
            textField: {
              size: 'small',
              slotProps: { inputLabel: { shrink: true } },
            },
          }}
        />
      </Box>

      {error && <Alert severity="error">加载数据失败: {error.message}</Alert>}

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography variant="h6">每日收入</Typography>
          <Typography variant="body2" color="text.secondary">
            合计：¥{totalIncome.toFixed(2)}
          </Typography>
        </Box>
        <BarChart
          loading={loading}
          xAxis={[
            { scaleType: 'band', data: xLabels, tickLabelStyle: { fontSize: 11 } },
          ]}
          series={[{ data: amounts, label: '收入 (元)', color: '#1976d2' }]}
          height={360}
          margin={{ left: 70, right: 20, top: 20, bottom: 60 }}
          yAxis={[{ valueFormatter: (v: number) => `¥${v}` }]}
        />
      </Box>
    </Box>
  )
}
